import React, { Suspense, useEffect } from 'react';

import {
  useLocation
} from "react-router-dom";

import { useDropzone } from 'react-dropzone';
import { CSVLink } from "react-csv";

import DefaultLayout from "../common/DefaultAdminLayout";
import EditExhibitorModal from "./modals/EditExhibitorModal"
import queryString from 'query-string'

import {
  useGetExhibitorsLazyQuery,
  useDeleteExhibitorMutation,
  ExhibitorModel,
} from '../../api'
import ConfirmModal from '../modals/ConfirmModal';
import MailNotificationModal from '../modals/MailNotificationModal';
import MailNotificationBulkModal from '../modals/MailNotificationBulkModal';
import { useAppContext } from '../../contexts/app';

import { makeAccountMailMessage, makeRemindMailMessage, calcMaxBadgesCount, createExhibitorsCSV, createBadgesCSV } from '../../utils'
import { exhibitionsRemindMailSubject } from '../../exhibitions';
import Pagination from '../parts/Pagination';

const limit = Number(process.env.REACT_APP_SCAN_LIMIT) || 300

const getIsFree = (search: string) => {
  const queryParams = queryString.parse(search);
  return queryParams.role === 'free'
}

const AdminHomePage = () => {
  const { uploadExhibitors } = useAppContext()
  const location = useLocation();
  const [showModal, setShowModal] = React.useState<string|undefined>(undefined);
  const [isUploading, setIsUploading] = React.useState<boolean>(false);
  const [selectedExhibitors, setSelectedExhibitors] = React.useState<ExhibitorModel[]>([]);
  const [fetchExhibitors, exhibitorsQuery] = useGetExhibitorsLazyQuery();
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: async (acceptedFiles) => {
      let params = new FormData();
      params.append('file', acceptedFiles[0]);
      setIsUploading(true)
      await uploadExhibitors(params).catch(() => {
        setIsUploading(false)
      })
      setIsUploading(false)
      await exhibitorsQuery.refetch()
    }
  })
  const [deleteExhibitorMutation] = useDeleteExhibitorMutation();

  const data = exhibitorsQuery.data
  
  useEffect(() => {
    fetchExhibitors({
      variables: { limit }
    })
  }, [fetchExhibitors]);
  
  if (!data) {
    return <DefaultLayout>
      <Suspense fallback={<div>...Loading</div>}>
        <div className='text-center'>
          <i className="fa fa-sync-alt fa-spin"></i>
        </div>
      </Suspense>
    </DefaultLayout>
  }

  // const isFree = getIsFree(location.search)
  const isFree = true

  const exhibitors = data.getExhibitors.records;

  const toNext = async () => {
    if (data.getExhibitors.LastEvaluatedKeyId) {
      await exhibitorsQuery.fetchMore({
        variables: {
          limit,
          ExclusiveStartKeyId: data.getExhibitors.LastEvaluatedKeyId,
        },
        updateQuery: (prev, current) => {
          if (current.fetchMoreResult) {
            const currentData = current.fetchMoreResult.getExhibitors
            currentData.records = [...prev.getExhibitors.records, ...currentData.records]
            return current.fetchMoreResult
          }
          return prev
        },
      })
    }
  }

  const onChangeExhibitors = async () => {
    setSelectedExhibitors([])
    setShowModal(undefined)
    await exhibitorsQuery.refetch()
  }

  const onChangeExhibitor = async () => {
    setShowModal(undefined)
    await exhibitorsQuery.refetch()
  }

  const deleteExhibitor = async (id: string) => {
    await deleteExhibitorMutation({
      variables: { id },
    })
    setSelectedExhibitors((current) => current.filter((exhibitor) => exhibitor.id !== id))
    setShowModal(undefined)
    await exhibitorsQuery.refetch()
  }

  const exhibitorsCSV = createExhibitorsCSV(exhibitors)

  const badgesCSV = createBadgesCSV(exhibitors)

  const isSelectedExhibitors = (id?: string) => {
    if (!id) {
      return selectedExhibitors.length === exhibitors.length
    } else {
      return !!selectedExhibitors.find((selected) => selected.id === id)
    }
  }

  const toggleSelectedExhibitors = (id?: string) => {
    if (!id) {
      if (isSelectedExhibitors()) {
        setSelectedExhibitors([])
      } else {
        setSelectedExhibitors(exhibitors.concat([]))
      }
    } else {
      const targetExhibitor = exhibitors.find((exhibitor) => exhibitor.id === id)
      if (!targetExhibitor) return
      if (isSelectedExhibitors(id)) {
        setSelectedExhibitors((current) => current.filter((exhibitor) => exhibitor.id !== id))
      } else {
        setSelectedExhibitors((current) => current.concat([targetExhibitor]))
      }
    }
  }

  return (
    <DefaultLayout isWide>
      <div className="btn-group">
        {isFree &&
          <div className="btn mr-3" onClick={() => setShowModal('create')}>新規</div>
        }
        <EditExhibitorModal
          type="create"
          isOpen={showModal === 'create'}
          handleCloseModal={() => setShowModal(undefined)}
          handleSubmit={() => onChangeExhibitor()}
        />
        {isFree &&
          <div {...getRootProps()} className={"btn mr-3" + (isUploading ? " loading" : "")}>
            <input {...getInputProps()} disabled={isUploading}/>
            <div>
              {isUploading ? <span>出展者作成中<i className="fa fa-sync-alt fa-spin"></i></span> : "出展者一括入力"}
            </div>
          </div>
        }
        <div className="btn mr-3"><CSVLink data={exhibitorsCSV} download="exhibitors.csv">出展者一括出力</CSVLink></div>
        <button className="btn mr-3" onClick={() => setShowModal("bulk-account")} disabled={!selectedExhibitors.length}>アカウント通知一括送信</button>
        <MailNotificationBulkModal
          type="account"
          exhibitors={selectedExhibitors}
          isOpen={showModal === "bulk-account"}
          handleCloseModal={() => setShowModal(undefined)}
          handleSubmit={() => onChangeExhibitors()}
        />
        <button className="btn mr-3" onClick={() => setShowModal("bulk-remind")} disabled={!selectedExhibitors.length}>リマインド通知一括送信</button>
        <MailNotificationBulkModal
          type="remind"
          exhibitors={selectedExhibitors}
          isOpen={showModal === "bulk-remind"}
          handleCloseModal={() => setShowModal(undefined)}
          handleSubmit={() => onChangeExhibitors()}
        />
        <div className="btn"><CSVLink data={badgesCSV} download="badges.csv">バッジ一括出力</CSVLink></div>
      </div>
      <table>
        <thead>
          <tr>
            <th>
              <input type="checkbox" checked={isSelectedExhibitors()} onChange={() => toggleSelectedExhibitors()} />
            </th>
            <th>出展者名</th>
            <th>展示会</th>
            <th className="text-center">通知</th>
            <th className="badge-header">バッジ<br/>登録数</th>
            {/* <th className="badge-header">バッジ<br/>登録数/最大数</th> */}
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {exhibitors.map((exhibitor, i) => {
            return <tr key={i}>
              <td>
                <input type="checkbox" checked={isSelectedExhibitors(exhibitor.id)} onChange={() => toggleSelectedExhibitors(exhibitor.id)} />
              </td>
              <td className="w-50">
                {exhibitor.companyName}
              </td>
              <td>
                {exhibitor.exhibition}
              </td>

              <td className="mail-link-group">
                <div>
                  <span className="mail-is-notified">
                    {exhibitor.accountNotifiedAt ? <i className="checked"/> : "未"}
                  </span>
                  <div className="mail-link" onClick={() => setShowModal(exhibitor.id+"-account")}>アカウント通知</div>
                  <MailNotificationModal 
                    title={exhibitor.companyName!}
                    type="account"
                    exhibitorId={exhibitor.id}
                    notifiedAt={exhibitor.accountNotifiedAt}
                    target={exhibitor.companyName!}
                    message={makeAccountMailMessage(exhibitor)}
                    to={exhibitor.email!}
                    subject={exhibitionsRemindMailSubject[exhibitor.exhibition]}
                    isOpen={showModal === exhibitor.id+"-account"}
                    handleCloseModal={() => setShowModal(undefined)}
                    handleSubmit={() => onChangeExhibitor()}
                  />
                </div>
                <div>
                  <span className="mail-is-notified">
                    {exhibitor.remindNotifiedAt ? <i className="checked"/> : "未"}
                  </span>
                  <div className="mail-link" onClick={() => setShowModal(exhibitor.id+"-remind")}>リマインド通知</div>
                  <MailNotificationModal 
                    title={exhibitor.companyName!}
                    type="remind"
                    exhibitorId={exhibitor.id}
                    notifiedAt={exhibitor.remindNotifiedAt}
                    target={exhibitor.companyName!}
                    message={makeRemindMailMessage(exhibitor)}
                    to={exhibitor.email!}
                    isOpen={showModal === exhibitor.id+"-remind"}
                    handleCloseModal={() => setShowModal(undefined)}
                    handleSubmit={() => onChangeExhibitor()}
                  />
                </div>
              </td>

              <td>{exhibitor.badges.length}</td>
              {/* <td>{exhibitor.badges.length} / {calcMaxBadgesCount(exhibitor.boothsCount!)}</td> */}

              <td className="tight-2">
                <div className="btn" onClick={() => setShowModal(exhibitor.id+"-edit")}>編集</div>
                <EditExhibitorModal
                  exhibitor={exhibitor}
                  isOpen={showModal === exhibitor.id+"-edit"}
                  handleCloseModal={() => setShowModal(undefined)}
                  handleSubmit={() => onChangeExhibitor()}
                />
              </td>

              <td className="tight-2">
                <div className="btn" onClick={() => setShowModal(exhibitor.id+"-delete")}>削除</div>
                <ConfirmModal 
                  title={`出展者 ${exhibitor.accountId} を削除します。よろしいですか？`}
                  isOpen={showModal === exhibitor.id+"-delete"}
                  handleCloseModal={() => setShowModal(undefined)}
                  handleSubmit={() => deleteExhibitor(exhibitor.id)}
                />
              </td>
            </tr>
          })}
        </tbody>
      </table>
      <Pagination hasNext={!!data.getExhibitors.LastEvaluatedKeyId} toNext={() => toNext()}/>
    </DefaultLayout>
  );
}

export default AdminHomePage;
